/**
 * Import Utilities
 * 
 * 提供 Excel、CSV 批次匯入功能
 */

import * as XLSX from 'xlsx';
import Papa from 'papaparse';

export interface ImportOptions {
  onProgress?: (progress: number) => void;
  onError?: (error: Error, row: number) => void;
}

export interface ImportResult<T> {
  success: T[];
  failed: Array<{ row: number; data: any; error: string }>;
  total: number;
}

/**
 * 解析 Excel 檔案
 */
export function parseExcelFile(file: File): Promise<any[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
        resolve(jsonData);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('檔案讀取失敗'));
    reader.readAsBinaryString(file);
  });
}

/**
 * 解析 CSV 檔案
 */
export function parseCSVFile(file: File): Promise<any[]> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        resolve(results.data as any[]);
      },
      error: (error) => {
        reject(error);
      },
    });
  });
}

/**
 * 驗證任務資料
 */
export function validateTaskData(row: any, index: number): { valid: boolean; error?: string; data?: any } {
  const url = row.url || row.URL || row.target_url || row['目標URL'];
  const keyword = row.keyword || row.關鍵字 || row['Keyword'];
  const priority = row.priority || row.優先級 || row['Priority'] || 'normal';
  const projectId = row.project_id || row.專案ID || row['Project ID'];

  if (!url || typeof url !== 'string' || url.trim() === '') {
    return { valid: false, error: `第 ${index + 1} 行：缺少 URL` };
  }

  // 驗證 URL 格式
  try {
    new URL(url);
  } catch {
    return { valid: false, error: `第 ${index + 1} 行：URL 格式不正確` };
  }

  // 驗證優先級
  const validPriorities = ['low', 'normal', 'high', 'urgent'];
  const normalizedPriority = priority.toLowerCase();
  if (!validPriorities.includes(normalizedPriority)) {
    return { valid: false, error: `第 ${index + 1} 行：優先級必須為 low/normal/high/urgent` };
  }

  return {
    valid: true,
    data: {
      target_url: url.trim(),
      keyword: keyword ? String(keyword).trim() : null,
      priority: normalizedPriority,
      project_id: projectId ? String(projectId).trim() : null,
      status: 'pending',
    },
  };
}

/**
 * 驗證關鍵字資料
 */
export function validateKeywordData(row: any, index: number): { valid: boolean; error?: string; data?: any } {
  const keyword = row.keyword || row.關鍵字 || row['Keyword'];

  if (!keyword || typeof keyword !== 'string' || keyword.trim() === '') {
    return { valid: false, error: `第 ${index + 1} 行：缺少關鍵字` };
  }

  const languageCode = row.language_code || row.語言 || row['Language'] || 'zh-TW';
  const locationCode = row.location_code || row.地區 || row['Location'] || 2158;
  const priority = row.priority || row.優先級 || row['Priority'] || 'normal';

  // 驗證優先級
  const validPriorities = ['low', 'normal', 'high', 'urgent'];
  const normalizedPriority = priority.toLowerCase();
  if (!validPriorities.includes(normalizedPriority)) {
    return { valid: false, error: `第 ${index + 1} 行：優先級必須為 low/normal/high/urgent` };
  }

  return {
    valid: true,
    data: {
      keyword: String(keyword).trim(),
      language_code: String(languageCode),
      location_code: Number(locationCode) || 2158,
      priority: normalizedPriority,
      is_active: 1,
    },
  };
}

/**
 * 批次匯入任務
 */
export async function importTasks(
  file: File,
  options: ImportOptions = {}
): Promise<ImportResult<any>> {
  const { onProgress, onError } = options;
  const result: ImportResult<any> = {
    success: [],
    failed: [],
    total: 0,
  };

  try {
    // 解析檔案
    let rows: any[];
    if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
      rows = await parseExcelFile(file);
    } else if (file.name.endsWith('.csv')) {
      rows = await parseCSVFile(file);
    } else {
      throw new Error('不支援的檔案格式，請使用 Excel (.xlsx, .xls) 或 CSV (.csv)');
    }

    result.total = rows.length;

    // 驗證並處理每一行
    for (let i = 0; i < rows.length; i++) {
      const validation = validateTaskData(rows[i], i);
      
      if (validation.valid && validation.data) {
        result.success.push(validation.data);
      } else {
        const errorMsg = validation.error || '未知錯誤';
        result.failed.push({
          row: i + 1,
          data: rows[i],
          error: errorMsg,
        });
        if (onError) {
          onError(new Error(errorMsg), i + 1);
        }
      }

      // 更新進度
      if (onProgress) {
        onProgress(((i + 1) / rows.length) * 100);
      }
    }
  } catch (error: any) {
    throw new Error(`匯入失敗: ${error.message}`);
  }

  return result;
}

/**
 * 批次匯入關鍵字
 */
export async function importKeywords(
  file: File,
  options: ImportOptions = {}
): Promise<ImportResult<any>> {
  const { onProgress, onError } = options;
  const result: ImportResult<any> = {
    success: [],
    failed: [],
    total: 0,
  };

  try {
    // 解析檔案
    let rows: any[];
    if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
      rows = await parseExcelFile(file);
    } else if (file.name.endsWith('.csv')) {
      rows = await parseCSVFile(file);
    } else {
      throw new Error('不支援的檔案格式，請使用 Excel (.xlsx, .xls) 或 CSV (.csv)');
    }

    result.total = rows.length;

    // 驗證並處理每一行
    for (let i = 0; i < rows.length; i++) {
      const validation = validateKeywordData(rows[i], i);
      
      if (validation.valid && validation.data) {
        result.success.push(validation.data);
      } else {
        const errorMsg = validation.error || '未知錯誤';
        result.failed.push({
          row: i + 1,
          data: rows[i],
          error: errorMsg,
        });
        if (onError) {
          onError(new Error(errorMsg), i + 1);
        }
      }

      // 更新進度
      if (onProgress) {
        onProgress(((i + 1) / rows.length) * 100);
      }
    }
  } catch (error: any) {
    throw new Error(`匯入失敗: ${error.message}`);
  }

  return result;
}

/**
 * 下載範本檔案（Excel）
 */
export function downloadTaskTemplate(): void {
  const template = [
    {
      'URL': 'https://example.com',
      '關鍵字': 'example keyword',
      '優先級': 'normal',
      '專案ID': '',
    },
  ];

  const worksheet = XLSX.utils.json_to_sheet(template);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, '任務範本');
  
  // 設定欄位寬度
  worksheet['!cols'] = [
    { wch: 30 }, // URL
    { wch: 20 }, // 關鍵字
    { wch: 10 }, // 優先級
    { wch: 15 }, // 專案ID
  ];

  XLSX.writeFile(workbook, '任務匯入範本.xlsx');
}

/**
 * 下載關鍵字範本檔案（Excel）
 */
export function downloadKeywordTemplate(): void {
  const template = [
    {
      '關鍵字': 'example keyword',
      '語言': 'zh-TW',
      '地區': '2158',
      '優先級': 'normal',
    },
  ];

  const worksheet = XLSX.utils.json_to_sheet(template);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, '關鍵字範本');
  
  // 設定欄位寬度
  worksheet['!cols'] = [
    { wch: 20 }, // 關鍵字
    { wch: 10 }, // 語言
    { wch: 10 }, // 地區
    { wch: 10 }, // 優先級
  ];

  XLSX.writeFile(workbook, '關鍵字匯入範本.xlsx');
}
